/**
 * Token-bound selectable card for monograph demo panels.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cn } from "../styling/index.js";
import { colorWithAlpha } from "./MonographPanelColor.js";
import type { MonographPanelTokens } from "./MonographPanelPrimitiveTypes.js";

export type MonographSelectableCardRole = "button" | "radio" | "tab";
export type MonographSelectableCardLayout = "column" | "row";

export interface MonographSelectableCardProps<Id extends string = string> {
  readonly tokens: MonographPanelTokens;
  readonly active: boolean;
  readonly children: React.ReactNode;
  readonly id?: Id;
  readonly onSelect?: (id: Id) => void;
  readonly onClick?: () => void;
  readonly color?: string;
  readonly inactiveBackground?: string;
  readonly activeBackground?: string;
  readonly role?: MonographSelectableCardRole;
  readonly disabled?: boolean;
  readonly ariaLabel?: string;
  readonly title?: string;
  readonly className?: string;
  readonly style?: React.CSSProperties;
  readonly layout?: MonographSelectableCardLayout;
  readonly gap?: React.CSSProperties["gap"];
  readonly alignItems?: React.CSSProperties["alignItems"];
  readonly justifyContent?: React.CSSProperties["justifyContent"];
  readonly width?: React.CSSProperties["width"];
  readonly minHeight?: React.CSSProperties["minHeight"];
  readonly minWidth?: React.CSSProperties["minWidth"];
  readonly padding?: React.CSSProperties["padding"];
}

export function MonographSelectableCard<Id extends string = string>({
  tokens,
  active,
  children,
  id,
  onSelect,
  onClick,
  color = tokens.accent,
  inactiveBackground = colorWithAlpha(tokens.void, "66"),
  activeBackground = colorWithAlpha(color, "1c"),
  role = "button",
  disabled = false,
  ariaLabel,
  title,
  className,
  style,
  layout = "column",
  gap = layout === "row" ? 10 : 4,
  alignItems = layout === "row" ? "center" : "stretch",
  justifyContent,
  width,
  minHeight,
  minWidth,
  padding = "10px 12px",
}: MonographSelectableCardProps<Id>): React.JSX.Element {
  const handleClick = (): void => {
    if (disabled) return;
    if (onClick) {
      onClick();
      return;
    }
    if (id !== undefined) {
      onSelect?.(id);
    }
  };
  const selectedProps =
    role === "tab"
      ? { "aria-selected": active }
      : role === "radio"
        ? { "aria-checked": active }
        : { "aria-pressed": active };

  return (
    <button
      type="button"
      role={role === "button" ? undefined : role}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      className={cn("csk-monograph-selectable-card", className)}
      onClick={handleClick}
      data-active={active ? "true" : "false"}
      {...selectedProps}
      style={{
        alignItems,
        background: active ? activeBackground : inactiveBackground,
        border: `1px solid ${active ? color : tokens.border}`,
        borderRadius: 10,
        boxShadow: active ? `0 0 14px ${colorWithAlpha(color, "28")}` : "none",
        color: active ? tokens.bright : tokens.text,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        flexDirection: layout,
        gap,
        justifyContent,
        minHeight,
        minWidth,
        opacity: disabled ? 0.58 : 1,
        padding,
        textAlign: "left",
        transition:
          "background 140ms ease, border-color 140ms ease, box-shadow 140ms ease, color 140ms ease",
        width,
        ...style,
      }}
    >
      {children}
    </button>
  );
}
